"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const format_1 = __importDefault(require("date-fns/format"));
const find_1 = __importDefault(require("lodash/find"));
const get_1 = __importDefault(require("lodash/get"));
const map_1 = __importDefault(require("lodash/map"));
const mobx_1 = require("mobx");
const getBookingDetails_1 = __importDefault(require("../../clients/booking/getBookingDetails"));
const API_STATE_1 = __importDefault(require("../../constants/app/API_STATE"));
const UNLIMITED_KM_1 = __importDefault(require("../../constants/app/UNLIMITED_KM"));
const DEFAULT_CURRENCY_1 = __importDefault(require("../../constants/date/DEFAULT_CURRENCY"));
const PAYMENT_TYPE_1 = __importDefault(require("../../constants/payment/PAYMENT_TYPE"));
const addOnFactory_1 = __importDefault(require("../../factories/addon/addOnFactory"));
const bookingPriceFactory_1 = __importDefault(require("../../factories/booking/bookingPriceFactory"));
const paymentDetailsFactory_1 = __importDefault(require("../../factories/booking/paymentDetailsFactory"));
const refundDetailsFactory_1 = __importDefault(require("../../factories/booking/refundDetailsFactory"));
const branchFactory_1 = __importDefault(require("../../factories/branch/branchFactory"));
const fleetGroupFactory_1 = __importDefault(require("../../factories/fleetgroup/fleetGroupFactory"));
const insuranceFactory_1 = __importDefault(require("../../factories/insurance/insuranceFactory"));
const getBookingStatus_1 = __importDefault(require("../../factories/utils/booking/getBookingStatus"));
const getLocalTimeStampFromGMT_1 = __importDefault(require("../../utils/store/getLocalTimeStampFromGMT"));
const mapInitialStateToStoreState_1 = __importDefault(require("../../utils/store/mapInitialStateToStoreState"));
/**
 * Booking Details by ID Domain Store
 */
class BookingDetailsStore {
    constructor(initialState, $env) {
        this.data = {};
        this.apiState = API_STATE_1.default.INITIAL;
        this.errorData = {};
        /**
         * Fetches booking details data from backend
         * @param errorHandler {function}
         */
        this.fetch = (0, mobx_1.flow)(function* (quoteId, errorHandler) {
            try {
                this.apiState = API_STATE_1.default.LOADING;
                this.data = {};
                this.errorData = {};
                const response = yield (0, getBookingDetails_1.default)(quoteId);
                // 200 http codes
                this.onSuccess(response);
            }
            catch (err) {
                // all other http codes
                this.onError(err, errorHandler);
            }
        });
        /**
         * on success callback
         * @param data
         */
        this.onSuccess = (data) => {
            this.data = data;
            this.apiState = API_STATE_1.default.SUCCESS;
        };
        /**
         * on error callback
         * @param err
         * @param errorHandler
         */
        this.onError = (err, errorHandler) => {
            this.apiState = API_STATE_1.default.FAILED;
            // when a request is cancelled, Axios promise is rejected
            if (err.response) {
                const errResponse = err.response;
                const { data, status } = errResponse;
                // stores axios error info in observable
                this.errorData = {
                    data,
                    status,
                };
                if (errorHandler) {
                    errorHandler(errResponse);
                }
            }
        };
        /**
         * Map initial state to store state
         * @param initialState
         */
        this.updateInitialState = (initialState) => {
            (0, mapInitialStateToStoreState_1.default)(this, initialState);
        };
        this.$env = $env;
        this.updateInitialState(initialState);
    }
    /**
     * Returns the loading status
     * @returns {Boolean}
     */
    get isLoading() {
        return this.apiState === API_STATE_1.default.LOADING;
    }
    /**
     * Returns if API fetch was success
     * @returns {Boolean}
     */
    get isSuccess() {
        return this.apiState === API_STATE_1.default.SUCCESS;
    }
    /**
     * Returns if API fetch has failed
     * @returns {Boolean}
     */
    get hasFailed() {
        return this.apiState === API_STATE_1.default.FAILED;
    }
    /**
     * Returns current config language
     * @returns {LANG_VALUES}
     */
    get language() {
        return this.$env.config.locale;
    }
    /**
     * Returns the booking status
     * @returns {string}
     */
    get status() {
        return (0, get_1.default)(this.data, 'status', '');
    }
    /**
     * Returns booking date + time in epoch format
     * @returns {Date}
     */
    get bookingDateTime() {
        return (0, get_1.default)(this.data, 'bookingDateTime', Date.now() / 1000);
    }
    /**
     * Returns formatted booking date eg. 12 June in KSA timezone
     * @returns {string}
     */
    get bookingDate() {
        const localTimeStamp = (0, getLocalTimeStampFromGMT_1.default)(this.bookingDateTime);
        return (0, format_1.default)(localTimeStamp, 'dd MMMM');
    }
    /**
     * Returns formatted booking time eg. 10:30 AM in KSA timezone
     * @returns {string}
     */
    get bookingTime() {
        const localTimeStamp = (0, getLocalTimeStampFromGMT_1.default)(this.bookingDateTime);
        return (0, format_1.default)(localTimeStamp, 'hh:mm a');
    }
    /**
     * Returns booking reference no
     * @returns {string}
     */
    get referenceNo() {
        return (0, get_1.default)(this.data, 'referenceNo', '');
    }
    /**
     * Returns providerReferenceNo
     * @returns {string}
     */
    get providerReferenceNo() {
        return (0, get_1.default)(this.data, 'providerReferenceNo', '');
    }
    /**
     * Returns the displayId
     * This will be shown on booking details & confirmation page
     * @returns {string}
     */
    get displayId() {
        return (0, get_1.default)(this.data, 'displayId', '');
    }
    /**
     * Returns true if the booking is cancellable
     * @returns {boolean}
     */
    get isCancellable() {
        return (0, get_1.default)(this.data, 'cancellable');
    }
    /**
     * Return payment type that was done by customer
     * @returns {string}
     */
    get paymentType() {
        return (0, get_1.default)(this.data, 'paymentType', '');
    }
    /**
     * Returns true if creditCardRequired is true
     * @returns {boolean}
     */
    get isCreditCardRequired() {
        return (0, get_1.default)(this.data, 'bookingExtraDetails.creditCardRequired', false);
    }
    /**
     * Returns true if isCarDelivery is true
     * @returns {boolean}
     */
    get isCarDelivery() {
        return (0, get_1.default)(this.data, 'bookingExtraDetails.carDelivery', false);
    }
    /**
     * Returns true if pay at branch
     * @returns {boolean}
     */
    get isPayAtBranch() {
        return this.paymentType === PAYMENT_TYPE_1.default.PAY_AT_BRANCH;
    }
    /**
     * Returns true if pay by card
     * @returns {boolean}
     */
    get isPayByCard() {
        return this.paymentType === PAYMENT_TYPE_1.default.CHECKOUT;
    }
    /**
     * Returns pick up date + time in epoch format
     * BE returns in GMT format
     * @returns {Date}
     */
    get pickupDateTime() {
        return (0, get_1.default)(this.data, 'pickupDateTime', Date.now() / 1000);
    }
    /**
     * Returns formatted date eg. 12 June
     * Converts GMT date to local KSA date
     * @returns {string}
     */
    get pickupDate() {
        const localTimeStamp = (0, getLocalTimeStampFromGMT_1.default)(this.pickupDateTime);
        return (0, format_1.default)(localTimeStamp, 'dd MMMM');
    }
    /**
     * Returns formatted time eg. 10:30 AM
     * Converts GMT to local KSA time
     * @returns {string}
     */
    get pickupTime() {
        const localTimeStamp = (0, getLocalTimeStampFromGMT_1.default)(this.pickupDateTime);
        return (0, format_1.default)(localTimeStamp, 'hh:mm a');
    }
    /**
     * Returns pick up branch id
     * @returns {number}
     */
    get pickupBranchId() {
        return (0, get_1.default)(this.data, 'pickupBranchId');
    }
    /**
     * Returns formatted pickup branch details
     * @returns {BranchFactoryType}
     */
    get pickupBranch() {
        const rawPickupBranch = (0, get_1.default)(this.data, 'pickupBranch', {});
        return (0, branchFactory_1.default)(rawPickupBranch, this.language);
    }
    /**
     * Returns drop off date + time in epoch format
     * BE returns in GMT format
     * @returns {Date}
     */
    get dropOffDateTime() {
        return (0, get_1.default)(this.data, 'dropOffDateTime', Date.now() / 1000);
    }
    /**
     * Returns formatted date eg. 12 June
     * Converts GMT date to local KSA date
     * @returns {string}
     */
    get dropOffDate() {
        const localTimeStamp = (0, getLocalTimeStampFromGMT_1.default)(this.dropOffDateTime);
        return (0, format_1.default)(localTimeStamp, 'dd MMMM');
    }
    /**
     * Returns formatted time eg. 10:30 AM
     * Converts GMT time to local KSA time
     * @returns {string}
     */
    get dropOffTime() {
        const localTimeStamp = (0, getLocalTimeStampFromGMT_1.default)(this.dropOffDateTime);
        return (0, format_1.default)(localTimeStamp, 'hh:mm a');
    }
    /**
     * Returns drop off branch id
     * @returns {number}
     */
    get dropOffBranchId() {
        return (0, get_1.default)(this.data, 'dropOffBranchId');
    }
    /**
     * Returns formatted dropoff branch details
     * @returns {BranchFactoryType}
     */
    get dropOffBranch() {
        const rawDropoffBranch = (0, get_1.default)(this.data, 'dropOffBranch', {});
        return (0, branchFactory_1.default)(rawDropoffBranch, this.language);
    }
    /**
     * Returns payment details for the booking
     * Card information in case Pay by Card
     * @returns {PaymentDetailsType}
     */
    get paymentDetails() {
        return (0, paymentDetailsFactory_1.default)((0, get_1.default)(this.data, 'paymentDetails', {}));
    }
    /**
     * Returns vehicle make, model and group details
     * @returns {FleetGroupFactoryType}
     */
    get vehicleDetails() {
        return (0, fleetGroupFactory_1.default)((0, get_1.default)(this.data, 'vehicleGroup', {}), this.language);
    }
    /**
     * Returns formatted selected insurance list
     * @return {Array}
     */
    get selectedInsuranceList() {
        const rawInsuranceList = (0, get_1.default)(this.data, 'insurances', []);
        return (0, map_1.default)(rawInsuranceList, (insurance) => (0, insuranceFactory_1.default)(insurance, this.language));
    }
    /**
     * Returns formatted selected addons
     * @returns {Array}
     */
    get selectedAddOnsList() {
        const rawAddOnsList = (0, get_1.default)(this.data, 'addons', []);
        return (0, map_1.default)(rawAddOnsList, (addOn) => (0, addOnFactory_1.default)(addOn, this.language));
    }
    /**
     * Returns true if has Unlimited Km.
     * add some comment..
     * @returns {Boolean}
     */
    get hasUnlimitedKm() {
        return !!(0, find_1.default)(this.selectedAddOnsList, (addOn) => {
            const addOnCode = (0, get_1.default)(addOn, 'code');
            return addOnCode === UNLIMITED_KM_1.default;
        });
    }
    /**
     * Returns necessary formatted price details for showing in UI
     * @returns {object}
     */
    get priceDetails() {
        const rawBookingPrice = (0, get_1.default)(this.data, 'bookingPrice', {});
        const soldDays = (0, get_1.default)(this.data, 'soldDays', '');
        const currency = (0, get_1.default)(this.data, 'currency', DEFAULT_CURRENCY_1.default);
        return (0, bookingPriceFactory_1.default)(rawBookingPrice, soldDays, currency);
    }
    /**
     * Returns the flight code if any
     * that was updated for the booking
     * @returns {string}
     */
    get flightNo() {
        return (0, get_1.default)(this.data, 'flightNo', '');
    }
    /**
     * Returns true if the booking has a flight code
     * @returns {boolean}
     */
    get hasFlightNo() {
        return !!this.flightNo;
    }
    /**
     * Returns driver details such as driver code, id etc
     * @returns {object}
     * To-Do: return type to be corrected once we implement this
     */
    get driverDetails() {
        return (0, get_1.default)(this.data, 'driver', {});
    }
    /**
     * Returns the booking status
     * @returns {string}
     */
    get bookingType() {
        var _a;
        return (0, getBookingStatus_1.default)(this.referenceNo, this.pickupDateTime, this.dropOffDateTime, this.status, (_a = this.agreementDetails) === null || _a === void 0 ? void 0 : _a.status);
    }
    /**
     * Returns the booking refund details
     * @returns {RefundDetails}
     */
    get refundDetails() {
        const currency = (0, get_1.default)(this.data, 'currency', DEFAULT_CURRENCY_1.default);
        return (0, refundDetailsFactory_1.default)((0, get_1.default)(this.data, 'refundDetails', {}), currency);
    }
    /**
     * Returns the agreement  details
     * @returns {AgreementDetailsType}
     */
    get agreementDetails() {
        return (0, get_1.default)(this.data, 'agreementDetails', {});
    }
    /**
     * Returns the agreement  details
     * @returns {ExtensionDetailsType}
     */
    get bookingExtensionDetails() {
        return (0, get_1.default)(this.data, 'bookingExtension', {});
    }
}
__decorate([
    mobx_1.observable
], BookingDetailsStore.prototype, "data", void 0);
__decorate([
    mobx_1.observable
], BookingDetailsStore.prototype, "apiState", void 0);
__decorate([
    mobx_1.observable
], BookingDetailsStore.prototype, "errorData", void 0);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "isLoading", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "isSuccess", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "hasFailed", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "language", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "status", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "bookingDateTime", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "bookingDate", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "bookingTime", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "referenceNo", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "providerReferenceNo", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "displayId", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "isCancellable", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "paymentType", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "isCreditCardRequired", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "isCarDelivery", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "isPayAtBranch", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "isPayByCard", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "pickupDateTime", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "pickupDate", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "pickupTime", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "pickupBranchId", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "pickupBranch", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "dropOffDateTime", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "dropOffDate", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "dropOffTime", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "dropOffBranchId", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "dropOffBranch", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "paymentDetails", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "vehicleDetails", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "selectedInsuranceList", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "selectedAddOnsList", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "hasUnlimitedKm", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "priceDetails", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "flightNo", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "hasFlightNo", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "driverDetails", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "bookingType", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "refundDetails", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "agreementDetails", null);
__decorate([
    mobx_1.computed
], BookingDetailsStore.prototype, "bookingExtensionDetails", null);
__decorate([
    mobx_1.action.bound
], BookingDetailsStore.prototype, "onSuccess", void 0);
__decorate([
    mobx_1.action.bound
], BookingDetailsStore.prototype, "onError", void 0);
__decorate([
    mobx_1.action.bound
], BookingDetailsStore.prototype, "updateInitialState", void 0);
exports.default = BookingDetailsStore;
